import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Plus, MessageCircle, Settings, MapPin, Loader, ChevronRight, Sparkles } from 'lucide-react';
import { ref, get } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import { getProvinceName } from '../utils/provinces';
import { getRecommendedAds } from '../utils/adMatching';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useUnreadCount } from '../hooks/useUnreadCount';

export default function MyProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [myAds, setMyAds] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [allProfiles, setAllProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const unreadCount = useUnreadCount();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/inloggen');
        return;
      }
      setUser(currentUser);
      
      const profileSnap = await get(ref(database, `vjb_profiles/${currentUser.uid}`));
      if (profileSnap.exists()) {
        setProfile(profileSnap.val());
      }
      
      const profilesSnap = await get(ref(database, 'vjb_profiles'));
      const profilesData = profilesSnap.exists() ? profilesSnap.val() : {};
      setAllProfiles(profilesData);
      
      const adsSnap = await get(ref(database, 'vjb_ads'));
      if (adsSnap.exists()) {
        const allAds = Object.entries(adsSnap.val()).map(([id, ad]) => ({ id, ...ad }));
        const userAds = allAds.filter(ad => ad.createdBy === currentUser.uid);
        setMyAds(userAds);
        
        if (profileSnap.exists()) {
          const recommended = getRecommendedAds(profileSnap.val(), allAds, currentUser.uid, 3);
          setRecommendations(recommended);
        }
      }
      
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const getCreatorInfo = (ad) => {
    const creatorProfile = allProfiles[ad.createdBy];
    return {
      name: creatorProfile?.displayName || 'Onbekend',
      photoUrl: creatorProfile?.photoUrl || null
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader className="animate-spin text-[#8146FF]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main className="flex-1 max-w-6xl mx-auto px-4 md:px-6 py-8 w-full">
        {/* Profile header */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
              {profile?.photoUrl ? (
                <img src={profile.photoUrl} alt={profile.displayName} className="w-full h-full object-cover" />
              ) : (
                <User size={40} className="text-white/40" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              {profile ? (
                <>
                  <h1 className="text-2xl font-bold mb-1 truncate">{profile.displayName}</h1>
                  <p className="text-white/60 flex items-center gap-1 mb-3">
                    <MapPin size={14} />
                    {profile.location?.city}, {getProvinceName(profile.location?.province)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {profile.instruments?.slice(0, 3).map((inst, idx) => (
                      <span key={idx} className="bg-[#8146FF]/20 text-[#8146FF] text-xs px-2 py-1 rounded-full">{inst}</span>
                    ))}
                    {profile.genres?.slice(0, 2).map((genre, idx) => (
                      <span key={idx} className="bg-white/10 text-white/60 text-xs px-2 py-1 rounded-full">{genre}</span>
                    ))}
                  </div>
                </>
              ) : (
                <div>
                  <h1 className="text-2xl font-bold mb-2">Welkom!</h1>
                  <p className="text-white/60 mb-4">Maak je profiel aan om te beginnen</p>
                </div>
              )}
            </div>

            <Link to="/profiel/bewerken" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition text-sm">
              <Settings size={16} />
              <span className="hidden sm:inline">Profiel bewerken</span>
            </Link>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Link to="/oproep/nieuw" className="bg-[#8146FF] hover:bg-[#6a35d9] rounded-2xl p-4 flex flex-col items-center gap-2 transition">
            <Plus size={24} />
            <span className="text-sm font-medium">Nieuwe oproep</span>
          </Link>
          
          <Link to="/zoeken" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-4 flex flex-col items-center gap-2 transition">
            <ChevronRight size={24} className="text-white/70" />
            <span className="text-sm font-medium text-white/70">Zoeken</span>
          </Link>
          
          <Link to="/berichten" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-4 flex flex-col items-center gap-2 transition relative">
            <MessageCircle size={24} className="text-white/70" />
            <span className="text-sm font-medium text-white/70">Berichten</span>
            {unreadCount > 0 && (
              <span className="absolute top-3 right-3 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>
        </div>

        {/* My Ads */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Mijn oproepen</h2>
            <Link to="/oproep/nieuw" className="text-[#8146FF] hover:text-[#a074ff] text-sm flex items-center gap-1">
              <Plus size={16} /> Nieuwe oproep
            </Link>
          </div>
          
          {myAds.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <p className="text-white/50 mb-4">Je hebt nog geen oproepen geplaatst</p>
              <Link to="/oproep/nieuw" className="inline-block bg-[#8146FF] hover:bg-[#6a35d9] px-6 py-2 rounded-full font-medium transition">
                Plaats je eerste oproep
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myAds.map((ad) => (
                <Link key={ad.id} to={`/oproep/${ad.id}`} className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#8146FF]/50 rounded-2xl p-4 transition">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold truncate">{ad.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ad.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'}`}>
                      {ad.status === 'active' ? 'Actief' : 'Gesloten'}
                    </span>
                  </div>
                  <p className="text-white/50 text-sm line-clamp-2 mb-3">{ad.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {ad.lookingFor?.slice(0, 2).map((item, idx) => (
                      <span key={idx} className="bg-[#8146FF]/20 text-[#8146FF] text-xs px-2 py-0.5 rounded-full">{item}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Recommendations */}
        {profile && recommendations.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Sparkles size={20} className="text-[#8146FF]" />
                Aanbevolen voor jou
              </h2>
              <Link to="/zoeken" className="text-[#8146FF] hover:text-[#a074ff] text-sm flex items-center gap-1">
                Bekijk alles <ChevronRight size={16} />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendations.map(({ ad, score, reasons }) => {
                const creator = getCreatorInfo(ad);
                return (
                  <Link key={ad.id} to={`/oproep/${ad.id}`} className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#8146FF]/50 rounded-2xl overflow-hidden transition">
                    <div className="aspect-[16/10] bg-gradient-to-br from-[#8146FF]/30 to-black relative overflow-hidden">
                      {creator.photoUrl ? (
                        <img src={creator.photoUrl} alt={creator.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center"><User size={40} className="text-white/20" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                      <div className="absolute top-3 right-3 bg-[#8146FF] text-white text-xs font-bold px-2 py-1 rounded-full">{score}% match</div>
                      <div className="absolute bottom-3 left-3 flex flex-wrap gap-1">
                        {ad.lookingFor?.slice(0, 2).map((item, idx) => (
                          <span key={idx} className="bg-[#8146FF] text-white text-xs px-2 py-1 rounded-full">{item}</span>
                        ))}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-1 group-hover:text-[#8146FF] transition truncate">{ad.title}</h3>
                      <p className="text-white/50 text-sm mb-3 line-clamp-1">{ad.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {reasons?.slice(0, 2).map((reason, idx) => (
                          <span key={idx} className="bg-white/10 text-white/60 text-xs px-2 py-0.5 rounded">{reason}</span>
                        ))}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {!profile && (
          <div className="bg-gradient-to-br from-[#8146FF]/20 to-black border border-[#8146FF]/30 rounded-2xl p-8 text-center">
            <h2 className="text-xl font-bold mb-2">Maak je profiel compleet</h2>
            <p className="text-white/60 mb-6">Voeg je instrumenten, genres en locatie toe om gepersonaliseerde aanbevelingen te krijgen.</p>
            <Link to="/profiel/bewerken" className="inline-block bg-[#8146FF] hover:bg-[#6a35d9] px-6 py-3 rounded-full font-medium transition">
              Profiel aanmaken
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
